import { useRef, useContext, useEffect, useCallback } from 'react';

import { ViewerContext } from '@vimeo/viewer';

import { CacheContext } from './CacheContext';
import {
  buildQueryString,
  MaybeArray,
  ResourceDeclaration,
  buildAcceptHeader,
  appendQueryString,
} from './libs';
import { usePage } from './PageProvider';
import { useFetchReducer } from './useFetchReducer';

type QueryOptions<Variables> = {
  variables?: Variables;
  resourceKey?: string;
  accept?: MaybeArray<ResourceDeclaration>;
};

/**
 * @deprecated Use endpoint-specific hooks from `@vimeo/gctl-api` instead.
 */
export function useQuery<Data = unknown, Variables extends Record<string, unknown> = {}>(
  endpoint: string,
  baseOptions: QueryOptions<Variables> = {},
) {
  const viewer = useContext(ViewerContext);
  const cache = useContext(CacheContext);
  const path = usePage();

  if (!cache) {
    throw new Error(`useQuery must be used within a <CacheContext.Provider />'s scope`);
  }

  const acceptRef = useRef(baseOptions.accept);
  const resource = baseOptions.variables
    ? endpoint + buildQueryString(baseOptions.variables)
    : endpoint;
  const cacheKey = baseOptions.resourceKey || resource;

  const [state, dispatch] = useFetchReducer<Data>();

  const getResource = useCallback(
    async (query: string) => {
      if (!viewer) {
        throw new Error(`Cannot make requests without a viewer object.`);
      }

      const response = await fetch(`https://${viewer.apiUrl}${query}`, {
        method: 'GET',
        headers: {
          Authorization: `jwt ${viewer.jwt}`,
          Accept: buildAcceptHeader(acceptRef.current),
          'Content-Type': 'application/json',
          'Vimeo-Page': path,
          'Accept-Language': viewer.locale,
        },
      });

      const json = await response.json();

      return json;
    },
    [viewer, path],
  );

  useEffect(() => {
    let stale = false;

    async function request() {
      if (!cache) {
        throw new Error(`useQuery must be used within a <CacheContext.Provider />'s scope`);
      }

      const cachedResource = cache.get(cacheKey);

      if (cachedResource) {
        dispatch({ type: 'CACHE_HIT', payload: cachedResource });
        return;
      }

      dispatch({ type: 'REQUEST' });

      try {
        const data = await getResource(resource);

        cache.set(cacheKey, data);

        if (!stale) {
          dispatch({ type: 'SUCCESS', payload: data });
        }
      } catch (err) {
        if (!stale) {
          dispatch({ type: 'FAILURE', payload: err });
        }
      }
    }

    request();

    return () => {
      stale = true;
    };
  }, [dispatch, getResource, cacheKey, resource, cache]);

  useEffect(() => {
    const unsubscribe = cache.subscribeToKey(cacheKey, (value) => {
      dispatch({ type: 'CACHE_HIT', payload: value });
    });

    return () => {
      unsubscribe();
    };
  }, [cacheKey, cache, dispatch]);

  const refetch = useCallback(
    async (options: QueryOptions<Variables> = {}) => {
      const nextOptions = { ...baseOptions, ...options };
      const nextResource = nextOptions.variables
        ? appendQueryString(endpoint, buildQueryString(nextOptions.variables))
        : endpoint;
      const nextCacheKey = nextOptions.resourceKey || nextResource;

      dispatch({ type: 'REQUEST' });

      try {
        const data = await getResource(nextResource);

        cache.set(nextCacheKey, data);
        dispatch({ type: 'SUCCESS', payload: data });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [dispatch, getResource, baseOptions, endpoint, cache],
  );

  return { ...state, refetch };
}
